/**
 * 香川県の背景ツアー「香川めぐり」コンテンツ。
 *
 * 軸は季節。5月の栗林公園から始めて、1年かけて翌年4月の丸亀城へ戻る。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 栗林公園を「日本三名園」と書かない。公式の表現は「特別名勝」「文化財指定庭園で日本一の広さ」。
 *  - 寒霞渓の「日本三大渓谷美」は県観光協会公式の表記としてのみ、引用の形で使う。
 *  - 丸亀城の石垣を「総高60m・日本一」と数値で断定しない。天守を「日本一小さい」とも書かない。
 *  - 銭形砂絵の制作者を断定しない。
 *  - 父母ヶ浜を「日本の夕陽百選」と書かない。いつでも鏡になるとも書かない。
 *  - 津嶋神社の参道橋は年2日しか架からない。
 *  - エンジェルロードは中余島より先が立入不可。
 *  - 直島など現代アート作品は商用利用が禁じられているため題材にしない。
 */

import type { StageTour } from '../types';

export const kagawaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'ritsurin',
      name: '栗林公園',
      area: '高松市・5月の朝',
      image: '/assets/scenery/kagawa/scenes/01_ritsurin.webp',
      hotspots: [
        {
          id: 'ritsurin-shakkei',
          x: 0.48,
          y: 0.42,
          title: '紫雲山を庭の一部にする',
          body: '背後の紫雲山を借景にした庭です。六つの池と十三の築山があり、池には和船が浮かびます。',
          source: 'https://www.my-kagawa.jp/ritsuringarden/feature/ritsuringarden/garden',
          mapQuery: '栗林公園',
        },
        {
          id: 'ritsurin-hirosa',
          x: 0.76,
          y: 0.28,
          title: '文化財指定庭園では日本一の広さ',
          body: '紫雲山を含めた面積はおよそ75ヘクタール（うち平庭部が約16.2ヘクタール）で、文化財に指定された庭園の中では日本一の広さです。昭和28年に特別名勝に指定されました。高松藩主松平家の下屋敷として使われていた庭です。',
          source: 'https://www.my-kagawa.jp/ritsuringarden/feature/ritsuringarden/garden',
          mapQuery: '栗林公園 掬月亭',
        },
      ],
    },
    {
      id: 'angel-road',
      name: 'エンジェルロード',
      area: '土庄町・6月の午前',
      image: '/assets/scenery/kagawa/scenes/02_angel_road.webp',
      hotspots: [
        {
          id: 'angel-sunamichi',
          x: 0.48,
          y: 0.52,
          title: '引き潮のときだけ現れる砂の道',
          body: '潮の満ち干きで現れたり消えたりする砂の道です。大切な人と手をつないで渡ると願いがかなうといわれます。',
          source: 'https://www.town.tonosho.kagawa.jp/gyosei/soshiki/shoko/4/3/172.html',
          mapQuery: 'エンジェルロード 小豆島',
        },
        {
          id: 'angel-yoshima',
          x: 0.76,
          y: 0.3,
          title: '島には無断で入れない',
          body: '弁天島・中余島・小余島・大余島の4つの島をまとめて「余島」と呼びます。弁天島から中余島へは渡れますが、中余島は私有地、大余島はYMCA専用の国際キャンプ場なので、無断で立ち入ることはできません。',
          source: 'https://www.town.tonosho.kagawa.jp/gyosei/soshiki/shoko/4/3/172.html',
          mapQuery: '土庄町 余島',
        },
      ],
    },
    {
      id: 'yashima',
      name: '屋島',
      area: '高松市・7月の昼',
      image: '/assets/scenery/kagawa/scenes/03_yashima.webp',
      hotspots: [
        {
          id: 'yashima-mesa',
          x: 0.48,
          y: 0.44,
          title: '屋根の形をした台地',
          body: 'およそ1,400万年前の火山活動で噴き出した溶岩が水平に流れ、まわりが削られてできたメサという地形です。南嶺の三角点は標高292.07メートル。昭和9年11月10日に国の天然記念物に指定されました。',
          source:
            'https://www.city.takamatsu.kagawa.jp/smph/kurashi/kosodate/bunka/bunkazai/shiteibunkazai/tennen/yashima.html',
          mapQuery: '屋島',
        },
        {
          id: 'yashima-shiseki',
          x: 0.76,
          y: 0.28,
          title: '城跡でもあり、合戦の舞台でもある',
          body: '同じ日に国の史跡にも指定されています。白村江の敗戦を受けて中大兄皇子が国防のために築かせた屋嶋城の跡があり、日本書紀には667年の条にその名が記されています。1185年2月の屋島の戦いの舞台にもなりました。山上には四国霊場84番札所の屋島寺。',
          source:
            'https://www.city.takamatsu.kagawa.jp/kurashi/kosodate/bunka/bunkazai/shiteibunkazai/shiseki/yashima.html',
          mapQuery: '屋島寺',
        },
      ],
    },
    {
      id: 'tsushima-jinja',
      name: '津嶋神社',
      area: '三豊市・8月の昼',
      image: '/assets/scenery/kagawa/scenes/04_tsushima_jinja.webp',
      hotspots: [
        {
          id: 'tsushima-hashi',
          x: 0.48,
          y: 0.48,
          title: '年に2日だけ架かる橋',
          body: '島に浮かぶ本殿へは、「しあわせ橋」を渡ってお参りします。この橋が架けられるのは、年に2日間だけです。',
          source: 'https://www.city.mitoyo.lg.jp/hotnews_old/hot06/hot2408/13079.html',
          mapQuery: '津嶋神社 三豊市',
        },
        {
          id: 'tsushima-taisai',
          x: 0.76,
          y: 0.3,
          title: '夏季大祭は8月4日・5日',
          body: '子どもや孫の健やかな成長を祈願する祭です。毎年およそ5万人の参拝客が集まり、4日には打ち上げ花火も上がります。',
          source: 'https://www.city.mitoyo.lg.jp/hotnews_old/hot06/hot2408/13079.html',
          mapQuery: '津嶋神社',
        },
      ],
    },
    {
      id: 'zenigata-sunae',
      name: '銭形砂絵',
      area: '観音寺市・9月の午後',
      image: '/assets/scenery/kagawa/scenes/05_zenigata_sunae.webp',
      hotspots: [
        {
          id: 'zenigata-ookisa',
          x: 0.48,
          y: 0.46,
          title: '周囲345メートルの寛永通宝',
          body: '琴弾公園にある、巨大な「寛永通宝」の砂絵です。周囲345メートル。琴弾山の山頂にある銭形展望台から見下ろすのもおすすめです。',
          source: 'https://www.city.kanonji.kagawa.jp/soshiki/21/333.html',
          mapQuery: '銭形砂絵 琴弾公園',
        },
        {
          id: 'zenigata-lightup',
          x: 0.76,
          y: 0.28,
          title: '一夜にして造られたと伝わる',
          body: '江戸時代の寛永のころ、藩主を歓迎するために一夜にして造られたとされています。毎日、日没から午後10時までライトアップされます。',
          source: 'https://www.city.kanonji.kagawa.jp/soshiki/21/333.html',
          mapQuery: '琴弾公園 展望台',
        },
      ],
    },
    {
      id: 'chichibugahama',
      name: '父母ヶ浜',
      area: '三豊市・10月の夕',
      image: '/assets/scenery/kagawa/scenes/06_chichibugahama.webp',
      hotspots: [
        {
          id: 'chichibu-kagami',
          x: 0.48,
          y: 0.56,
          title: '鏡になる条件',
          body: '干潮になると現れる干潟に、景色が鏡のように映ります。おすすめは「風のない、干潮時」で、時間は「日の入り前後、約30分間のマジックアワー」です。',
          source: 'https://www.my-kagawa.jp/point/3213/',
          mapQuery: '父母ヶ浜',
        },
        {
          id: 'chichibu-shizen',
          x: 0.76,
          y: 0.3,
          title: 'ここは自然共生サイト',
          body: '令和7年度に自然共生サイトに認定されました。浜辺にはハマボウフウなどの海浜植物や、貝、カニなど数多くの動植物が生息しています。地元の有志団体「ちちぶの会」による保全活動が長年続けられています。',
          source:
            'https://www.city.mitoyo.lg.jp/kakuka/shiminkankyou/eisei/2/kankyouseisaku/14244.html',
          mapQuery: '父母ヶ浜海水浴場',
        },
      ],
    },
    {
      id: 'kankakei',
      name: '寒霞渓',
      area: '小豆島町・11月の夕',
      image: '/assets/scenery/kagawa/scenes/07_kankakei.webp',
      hotspots: [
        {
          id: 'kankakei-kiseki',
          x: 0.48,
          y: 0.44,
          title: '1,300万年かけてできた谷',
          body: 'およそ1,300万年の歳月が作った渓谷美で、風雨の浸食でできた奇岩が連なります。垂直にそそり立つ大岩壁のあいだを、ロープウェイが行きます。',
          source: 'https://www.my-kagawa.jp/point/219/',
          mapQuery: '寒霞渓',
        },
        {
          id: 'kankakei-hyouka',
          x: 0.76,
          y: 0.28,
          title: '「日本三大渓谷美のひとつ」とされる',
          body: '香川県観光協会の公式サイトは「日本三大渓谷美のひとつに数えられ」と紹介しています。選定の主体は示されていません。',
          source: 'https://www.my-kagawa.jp/point/219/',
          mapQuery: '寒霞渓ロープウェイ',
        },
      ],
    },
    {
      id: 'seto-ohashi',
      name: '瀬戸大橋',
      area: '坂出市・12月の夜',
      image: '/assets/scenery/kagawa/scenes/08_seto_ohashi.webp',
      hotspots: [
        {
          id: 'setoohashi-nidan',
          x: 0.5,
          y: 0.42,
          title: '上が道路、下が鉄道',
          body: '3つの吊橋、2つの斜張橋、1つのトラス橋でできています。上が自動車道路、下が鉄道の二段構造で、道路鉄道併用橋としては世界最大級です。',
          source: 'https://www.city.sakaide.lg.jp/soshiki/seisaku/setoohashibridge.html',
          mapQuery: '瀬戸大橋',
        },
        {
          id: 'setoohashi-kaitsu',
          x: 0.76,
          y: 0.28,
          title: '1988年4月10日に開通',
          body: '全長9,368メートル、道路鉄道併用部分の総延長は13.1キロ。途中で与島を経由します。',
          source: 'https://www.city.sakaide.lg.jp/soshiki/seisaku/setoohashibridge.html',
          mapQuery: '与島パーキングエリア',
        },
      ],
    },
    {
      id: 'kotohiragu',
      name: '金刀比羅宮',
      area: '琴平町・1月の朝',
      image: '/assets/scenery/kagawa/scenes/09_kotohiragu.webp',
      hotspots: [
        {
          id: 'konpira-ishidan',
          x: 0.48,
          y: 0.5,
          title: '御本宮まで785段',
          body: '表参道から御本宮までの石段は全785段。海抜251メートルに鎮座しています。眼下には讃岐平野が広がります。',
          source: 'https://www.konpira.or.jp/articles/20200616_guide/article.htm',
          mapQuery: '金刀比羅宮',
        },
        {
          id: 'konpira-okusha',
          x: 0.76,
          y: 0.26,
          title: 'さらに上に奥社がある',
          body: '御祭神は大物主神と崇徳天皇。古くから農業・殖産・医薬・海上守護の神とされます。石段1,368段目には奥社（厳魂神社）があります。',
          source: 'https://www.konpira.or.jp/',
          mapQuery: '金刀比羅宮 奥社',
        },
      ],
    },
    {
      id: 'marugamejo',
      name: '丸亀城',
      area: '丸亀市・4月の朝',
      image: '/assets/scenery/kagawa/scenes/10_marugamejo.webp',
      hotspots: [
        {
          id: 'marugame-ishigaki',
          x: 0.48,
          y: 0.5,
          title: '高さ20メートル以上の高石垣',
          body: '北側、瀬戸内海側の石垣は、高さ20メートル以上の高石垣です。丸亀城は「石垣の名城」と言われるほど、石垣で知られています。',
          source: 'https://www.city.marugame.lg.jp/site/castle/2920.html',
          relatedItemId: 'honetsukidori',
          mapQuery: '丸亀城',
        },
        {
          id: 'marugame-tsumikata',
          x: 0.76,
          y: 0.28,
          title: '石垣の積み方の見本市',
          body: '切り込みハギ、打ち込みハギ、野面積み、算木積みなど、さまざまな積み方の石垣を一か所で見られます。主な石垣は、割って加工した石を積み上げる打ち込みハギです。',
          source: 'https://www.city.marugame.lg.jp/site/castle/2920.html',
          mapQuery: '丸亀城 天守',
        },
      ],
    },
  ],
};
